/**
 * CONVOY TOW — styles.
 *
 * Design source of truth: design-explorations/pink-bead/tow.html (gitignored) —
 * the `.track` rule, `@keyframes tow`, and variant `.v4` (Convoy) with
 * `@keyframes carriage`.
 *
 * The mock tows a fixed 980px plane by -980px. Here the plane is the real
 * viewport, so the same idea is expressed as a 100%-wide track whose two slots
 * are each `flex: 0 0 100%` and therefore overflow it: sliding the track by
 * -100% of ITS OWN width lands slot 2 exactly where slot 1 was. Same rigid
 * coupling, no hardcoded page width.
 *
 * Every timing is imported from the pin-flight contract. None are re-typed:
 * the tow and the flying pin have to agree to the millisecond (see TOW_END_MS).
 */
import { keyframes } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { FLIGHT_TIMING } from '../pin-flight/pin-flight-contract';

/* ------------------------------------------------------------------ *
 * Timing
 * ------------------------------------------------------------------ */

/** The mock's tow easing (`.v4` inherits it from the base `.track` rule). */
const TOW_EASE = 'cubic-bezier(.16,.84,.34,1)';

/** `.v4` carriage easing — the slight overshoot as each card catches up. */
const CARRIAGE_EASE = 'cubic-bezier(.2,1.2,.4,1)';
const CARRIAGE_DURATION_MS = 520;
const CARRIAGE_STAGGER_MS = 60;
/** The first carriage starts after the tow is well underway (mock `--c`). */
const CARRIAGE_START_MS = 700;
/**
 * How many cards get their own stagger step. Past this they all share the last
 * delay: they are below the fold on arrival, so a longer ripple would only be
 * animating things nobody is looking at.
 */
const CARRIAGE_COUNT = 10;

/**
 * HARD CONSTRAINT — the tow must END exactly at TOUCHDOWN.
 *
 * A separate flying pin plants on the map at TOUCHDOWN. It commits to a POINT
 * on the map, so if the pages were still settling afterwards the map would
 * slide out from under a pin that had already landed. TOW_START + TOW_DURATION
 * === TOUCHDOWN is therefore load-bearing, not a coincidence — it is asserted
 * below rather than left as a comment for someone to violate quietly.
 */
export const TOW_END_MS = FLIGHT_TIMING.TOW_START + FLIGHT_TIMING.TOW_DURATION;

if (
  process.env.NODE_ENV === 'development' &&
  TOW_END_MS !== FLIGHT_TIMING.TOUCHDOWN
) {
  // eslint-disable-next-line no-console
  console.error(
    `[page-tow] BROKEN INVARIANT: TOW_START (${FLIGHT_TIMING.TOW_START}) + ` +
      `TOW_DURATION (${FLIGHT_TIMING.TOW_DURATION}) = ${TOW_END_MS}, but ` +
      `TOUCHDOWN is ${FLIGHT_TIMING.TOUCHDOWN}. The pin will plant while the ` +
      `pages are still moving. Fix the contract, not this file.`,
  );
}

/**
 * When the last carriage has landed, measured from the same t=0 as the tow.
 * The track is gone at TOUCHDOWN (above) but the cards are still trailing in,
 * so the chassis has to outlive the tow by this much before it can be dropped.
 */
export const CARRIAGE_END_MS =
  CARRIAGE_START_MS +
  CARRIAGE_STAGGER_MS * (CARRIAGE_COUNT - 1) +
  CARRIAGE_DURATION_MS;

/* ------------------------------------------------------------------ *
 * Keyframes
 * ------------------------------------------------------------------ */

/** The mock's `@keyframes tow`, in viewport-relative units. */
const tow = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(-100%); }
`;

/** The mock's `@keyframes carriage`. */
const carriage = keyframes`
  from { transform: translateX(66px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

/* ------------------------------------------------------------------ *
 * Slots
 * ------------------------------------------------------------------ */

/**
 * A card's grid item, addressed structurally.
 *
 * The results page is owned by another agent, so there is no `data-` hook to
 * key off and none can be added. `.MuiCard-root` is the stable, documented MUI
 * class of the shared listing card's root; `:has(> …)` selects the wrapper
 * element the page puts each card in — which is the grid item, and therefore
 * the thing whose index is the card's position in the results.
 *
 * `:nth-of-type` rather than `:nth-child` because emotion warns that the latter
 * is SSR-unsafe (it logged on every tow). The grid items are all divs, so the
 * two count identically here; if that ever stops being true the only cost is a
 * stagger that counts slightly differently, which is cosmetic.
 *
 * If `:has()` is unsupported the whole rule is dropped and the cards simply
 * arrive with their page. The tow itself does not depend on this.
 */
const CARD_SLOT = '& *:has(> .MuiCard-root)';
const CARD_SLOT_NTH = (i: number) => `${CARD_SLOT}:nth-of-type(${i})`;

/* ------------------------------------------------------------------ *
 * The chassis
 * ------------------------------------------------------------------ */

/**
 * The clip window. `frozenHeight` is the scroll container's visible height,
 * captured at tow start: pinning it means both pages are exactly one screen
 * tall for the duration, so the track is a rigid body and the scroll container
 * cannot scroll mid-tow (its content is now exactly its own height).
 *
 * Idle it is `undefined` — a bare div with no styles, which is the whole point:
 * this chassis must be invisible to layout when it is not towing.
 * @param frozenHeight - Scroll container's visible height captured at tow
 *   start, or null when idle.
 * @returns The clip-window sx while towing, or undefined when idle.
 */
export function viewportSx(
  frozenHeight: number | null,
): SxProps<Theme> | undefined {
  if (frozenHeight === null) return undefined;
  return {
    position: 'relative',
    overflow: 'hidden',
    height: `${frozenHeight}px`,
  };
}

/**
 * The track — two pages, one rigid body.
 *
 * `both` back-fills translateX(0) through the delay, which is what holds the
 * home page still for the first TOW_START ms while the pin climbs out of the
 * Near field. Outside a tow the transform is `none`, NOT `translateX(0)`: a
 * transform of any value would make this element the containing block for the
 * pages' `position: fixed`/sticky descendants, which is exactly what we want
 * DURING the tow (chrome rides with its own page) and must not leak after it.
 * @param towing - Whether a tow is currently in progress.
 * @param delayMs - Delay before the tow animation starts, in ms.
 * @returns The track sx for the current phase.
 */
export function trackSx(towing: boolean, delayMs: number): SxProps<Theme> {
  if (!towing) return { transform: 'none' };
  return {
    display: 'flex',
    width: '100%',
    height: '100%',
    transform: 'none',
    animation: `${tow} ${FLIGHT_TIMING.TOW_DURATION}ms ${TOW_EASE} ${delayMs}ms both`,
  };
}

/** Geometry shared by both slots while the track is coupled. */
const towingSlotSx = {
  flex: '0 0 100%',
  minWidth: 0,
  height: '100%',
  overflow: 'hidden',
} as const;

/**
 * The page being hauled away.
 *
 * It stays mounted and live for the length of the tow, so it is made inert:
 * `pointer-events: none` stops clicks landing on a page that is leaving.
 *
 * `scrollOffset` compensates for the scroll position the container loses when
 * the viewport freezes to one screen (its content shrinks, so the browser
 * clamps scrollTop to 0). Shifting the slot up by exactly that amount keeps the
 * outgoing page visually where the user left it. At the usual scrollOffset of 0
 * this is `none`, which keeps the leaving page's own sticky chrome working.
 * @param scrollOffset - scrollTop the container lost when the viewport froze.
 * @returns The outgoing slot's sx.
 */
export function outgoingSlotSx(scrollOffset: number): SxProps<Theme> {
  return {
    ...towingSlotSx,
    pointerEvents: 'none',
    transform: scrollOffset ? `translateY(-${scrollOffset}px)` : 'none',
  };
}

/**
 * The page being hauled in, and its carriages.
 *
 * `arriving` outlives `towing`: the track is dismantled at TOUCHDOWN but the
 * cards are still trailing in until CARRIAGE_END_MS, so this slot keeps the
 * carriage rules — and, critically, keeps them BYTE-IDENTICAL across the phase
 * change. A CSS animation survives a class swap as long as its name/duration/
 * delay are unchanged; alter any of them and every card would jump back to its
 * first frame at TOUCHDOWN.
 * @param towing - Whether the track is still coupled (pre-TOUCHDOWN).
 * @param arriving - Whether the incoming page/carriages are still animating.
 * @param delayMs - Base carriage animation delay, in ms.
 * @returns The incoming slot's sx, or undefined once arrival has finished.
 */
export function incomingSlotSx(
  towing: boolean,
  arriving: boolean,
  delayMs: number,
): SxProps<Theme> | undefined {
  if (!arriving) return undefined;

  const carriageDelays = Object.fromEntries(
    Array.from({ length: CARRIAGE_COUNT }, (_, i) => [
      CARD_SLOT_NTH(i + 1),
      { animationDelay: `${delayMs + i * CARRIAGE_STAGGER_MS}ms` },
    ]),
  );

  return {
    ...(towing ? towingSlotSx : null),
    [CARD_SLOT]: {
      animation: `${carriage} ${CARRIAGE_DURATION_MS}ms ${CARRIAGE_EASE} both`,
      // Cards past the stagger window all ride the last step.
      animationDelay: `${delayMs + (CARRIAGE_COUNT - 1) * CARRIAGE_STAGGER_MS}ms`,
    },
    ...carriageDelays,
  };
}

/**
 * The carriage delay for a tow whose t=0 was `shiftMs` ago.
 * @param shiftMs - How long ago the tow's t=0 was, in ms.
 * @returns Remaining delay before the carriages start, in ms (min 0).
 */
export function carriageDelayMs(shiftMs: number): number {
  return Math.max(0, CARRIAGE_START_MS - shiftMs);
}

/**
 * The tow delay for a tow whose t=0 was `shiftMs` ago.
 * @param shiftMs - How long ago the tow's t=0 was, in ms.
 * @returns Remaining delay before the tow starts, in ms (min 0).
 */
export function towDelayMs(shiftMs: number): number {
  return Math.max(0, FLIGHT_TIMING.TOW_START - shiftMs);
}
